import React, { useId, useState } from 'react';
import DisclosureArrow from './DisclosureArrow';
import { FOCUS_RING, FOCUS_RING_INSET } from './focusRing';
import { isRenderableNode } from './renderableNode';

interface AccordionBaseProps {
    className?: string;
    children?: React.ReactNode;
    /**
     * Visual style. `default` is the flat list row (arrow leading, row dividers,
     * hover background). `sunken` draws the section as a sunken card (dimmed
     * rounded container, arrow pinned right, no dividers/hover) — used for stacked
     * detail sections in side panels and detail pages (see `CollapsibleSection`).
     */
    variant?: 'default' | 'sunken';
    /**
     * Content pinned to the right of the header, OUTSIDE the toggle button — so it may
     * be interactive (e.g. a Checkbox) without nesting a button inside a button.
     */
    trailing?: React.ReactNode;
    defaultExpanded?: boolean;
    isExpanded?: boolean;
    onToggle?: (expanded: boolean) => void;
    /** Disables the toggle (and its hover). Trailing controls manage their own state. */
    disabled?: boolean;
    /**
     * Pin the header row to the top of the nearest scroll container so it stays
     * reachable while the body scrolls. Needs a scroll ancestor that clips without
     * trapping sticky — e.g. a `<Surface overflow="clip">` (`overflow: hidden` traps it).
     */
    stickyHeader?: boolean;
    /** Offset (px) from the top of the scroll container when `stickyHeader` is set. */
    stickyTopOffset?: number;
    /**
     * Animate the expand/collapse height (default `true`). Set `false` for an instant
     * open — the body isn't clipped, so a `position: sticky` child pins immediately with
     * no post-animation settle.
     */
    animate?: boolean;
}

/**
 * Requires at least one of `title` (simple text header) or `header` (rich ReactNode
 * content for the toggle area, e.g. avatar + badges) so the toggle is never empty.
 */
type AccordionProps = AccordionBaseProps &
    ({ title: string; header?: React.ReactNode } | { title?: string; header: React.ReactNode });

const Accordion: React.FC<AccordionProps> = ({
    title,
    header,
    className,
    children,
    trailing,
    variant = 'default',
    defaultExpanded = false,
    isExpanded,
    onToggle,
    disabled = false,
    stickyHeader = false,
    stickyTopOffset = 0,
    animate = true,
}) => {
    const sunken = variant === 'sunken';
    const instanceId = useId();
    const bodyId = `${instanceId}-body`;
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

    // Use controlled state if isExpanded is provided, otherwise use internal state
    const expanded = isExpanded !== undefined ? isExpanded : internalExpanded;

    // The instant path only renders the body while expanded; don't advertise
    // aria-controls when the referenced element isn't in the DOM.
    const bodyRendered = animate || (expanded && !!children);

    // The body clips overflow so the expand/collapse height animation works. Once fully
    // expanded we release the clip (after the transition) so a `position: sticky` child
    // inside can pin — `overflow: hidden` would otherwise trap it.
    //
    // Owned entirely by the transition: the same `onTransitionEnd` that sets it on expand
    // clears it on collapse, so no effect has to mirror `expanded` into it.
    const [bodySettled, setBodySettled] = useState(false);

    const handleToggle = () => {
        const newExpanded = !expanded;
        if (isExpanded === undefined) {
            setInternalExpanded(newExpanded);
        }
        onToggle?.(newExpanded);
    };

    const containerClasses = [
        'flex flex-col w-full text-left',
        sunken ? 'rounded-xl bg-[var(--malachite-card-dimmed-background)] p-[4px]' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    // The whole header row is a `group` so the disclosure arrow darkens on hover; the
    // hover background and arrow react to hovering anywhere on the row. The sunken
    // variant carries its own card chrome instead of row dividers/hover.
    const rowBackground = sunken
        ? 'bg-transparent'
        : expanded
          ? 'bg-[var(--malachite-buttonlist-hover-background)]'
          : disabled
            ? 'bg-transparent'
            : 'bg-transparent hover:bg-[var(--malachite-buttonlist-hover-background)]';
    const headerRowClasses = [
        // `group` only when enabled, so the arrow doesn't darken on hover while disabled.
        disabled ? '' : 'group',
        'flex items-center w-full transition-colors',
        sunken ? '' : 'border-0 border-b border-solid border-[var(--malachite-card-border)]',
        stickyHeader ? 'sticky z-10' : '',
        rowBackground,
    ]
        .filter(Boolean)
        .join(' ');
    // Full-bleed default rows use an inset ring; the padded sunken toggle fits an outset one.
    const toggleButtonClasses = [
        'flex flex-1 items-center min-w-0 appearance-none border-0 bg-transparent text-left cursor-pointer select-none disabled:cursor-not-allowed',
        sunken
            ? `gap-[16px] px-[16px] py-[12px] rounded-lg ${FOCUS_RING}`
            : `gap-[12px] p-[16px] ${FOCUS_RING_INSET}`,
    ].join(' ');

    // A pinned header needs an opaque background so the scrolling body doesn't bleed
    // through. Inline (not a Tailwind arbitrary class) so the offset always applies.
    const headerRowStyle: React.CSSProperties = stickyHeader
        ? { top: `${stickyTopOffset}px`, backgroundColor: 'var(--malachite-card-background)' }
        : {};

    return (
        <div className={containerClasses}>
            <div className={headerRowClasses} style={headerRowStyle} data-accordion-header="">
                {/* Toggle area — arrow + header/title. Interactive trailing sits outside it. */}
                <button
                    type="button"
                    className={toggleButtonClasses}
                    onClick={handleToggle}
                    aria-expanded={expanded}
                    aria-controls={bodyRendered ? bodyId : undefined}
                    disabled={disabled}
                >
                    {sunken ? null : <DisclosureArrow expanded={expanded} />}
                    {header ?? (
                        <span className="flex-1 min-w-0 truncate font-medium text-[13px] tracking-[0.13px] text-[var(--malachite-text-primary)]">
                            {title}
                        </span>
                    )}
                    {sunken ? <DisclosureArrow expanded={expanded} /> : null}
                </button>
                {isRenderableNode(trailing) && (
                    <div className="shrink-0 flex items-center pr-[16px] text-[var(--malachite-text-secondary)]">
                        {trailing}
                    </div>
                )}
            </div>

            {animate ? (
                /* Height animates via grid-template-rows (0fr → 1fr); body clips during.
                   grid-cols-[minmax(0,1fr)] pins the single column to the track width so
                   wide, non-wrapping children (e.g. a table with long cell text) can't grow
                   the implicit `auto` column to their max-content and overflow the container. */
                <div
                    id={bodyId}
                    className="grid grid-cols-[minmax(0,1fr)] w-full transition-[grid-template-rows] duration-200 ease-in-out"
                    style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
                    onTransitionEnd={(event) => {
                        if (event.propertyName === 'grid-template-rows') {
                            setBodySettled(expanded);
                        }
                    }}
                >
                    <div
                        className={bodySettled && expanded ? 'overflow-visible' : 'overflow-hidden'}
                    >
                        {children && (
                            <div
                                className={
                                    sunken
                                        ? 'pt-[4px]'
                                        : 'border-b border-solid border-[var(--malachite-card-border)]'
                                }
                            >
                                {children}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                /* Instant open — no clip, so a sticky child inside pins immediately. */
                expanded &&
                children && (
                    <div
                        id={bodyId}
                        className={
                            sunken
                                ? 'pt-[4px]'
                                : 'border-b border-solid border-[var(--malachite-card-border)]'
                        }
                    >
                        {children}
                    </div>
                )
            )}
        </div>
    );
};

export default Accordion;
export { Accordion };
export type { AccordionProps };
